import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingFlat as TrendingFlatIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  WiDaySunny,
  WiCloudy,
  WiRain,
  WiSnow,
  WiThunderstorm,
  WiFog,
  WiDayCloudyHigh,
  WiNightClear,
  WiNightCloudy,
  WiBarometer,
  WiThermometer,
  WiStrongWind,
  WiHorizonAlt,
  WiWindDeg,
} from 'react-icons/wi';

/**
 * WeatherCard Component
 * 
 * Displays aviation weather for a specific airport with VFR/IFR/LIFR flight category,
 * current conditions, trend indicators, and alerts.
 * 
 * Features:
 * - Auto-refresh every 15 minutes (configurable)
 * - Color-coded flight category badges
 * - Current conditions grid (ceiling, visibility, wind, temp, altimeter)
 * - Weather trend indicator (improving/stable/worsening)
 * - Alerts section for significant weather
 * - Loading and error states
 * - Manual refresh capability
 * - Raw METAR display
 * - Responsive design with larger cards for mobile
 * - Dark mode compatible
 * 
 * Props:
 * @param {string} icao - ICAO airport code (e.g., "KJFK")
 * @param {string} title - Optional card title (e.g., "Departure")
 * @param {boolean} autoRefresh - Enable auto-refresh (default: true)
 * @param {number} refreshInterval - Refresh interval in ms (default: 900000 = 15min)
 */
const WeatherCard = ({ 
  icao, 
  title = null, 
  autoRefresh = true, 
  refreshInterval = 900000 // 15 minutes default
}) => {
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const normalizeWeatherData = (data) => {
    if (!data) return null;

    const parseNumber = (value) => {
      if (value === null || value === undefined) return null;
      const num = Number(String(value).replace(/[^0-9.+-]/g, ''));
      return Number.isFinite(num) ? num : null;
    };

    const tempF = data.conditions?.temperature_f ?? data.temperature_f ?? null;
    const tempFValue = parseNumber(tempF);
    const temperature_c = tempFValue !== null
      ? Math.round(((tempFValue - 32) * 5) / 9)
      : null;

    const altimeter = data.conditions?.altimeter ?? data.altimeter ?? null;
    let altimeterInHg = parseNumber(altimeter);
    let altimeter_hpa = null;

    if (altimeterInHg !== null) {
      // If value looks like hPa (e.g., 1013), convert to inHg
      if (altimeterInHg > 100) {
        altimeter_hpa = Math.round(altimeterInHg);
        altimeterInHg = altimeterInHg / 33.8639;
      } else {
        altimeter_hpa = Math.round(altimeterInHg * 33.8639);
      }
    }

    return {
      airport: data.airport || data.icao,
      flight_category: data.flight_category,
      ceiling: data.conditions?.ceiling_ft ?? data.ceiling ?? null,
      visibility: data.conditions?.visibility_sm ?? data.visibility ?? null,
      wind: data.conditions?.wind ?? data.wind ?? null,
      temperature_c,
      temperature_f: tempFValue,
      altimeter_inhg: altimeterInHg,
      altimeter_hpa,
      weather: data.conditions?.weather ?? data.weather ?? null,
      trend: data.trend?.direction ?? data.trend ?? null,
      alerts: Array.isArray(data.alerts)
        ? data.alerts.map((alert) => (alert?.message ? alert.message : alert))
        : [],
      raw_text: data.metar_raw ?? data.raw_text ?? null,
    };
  };

  // Fetch weather data from API
  const fetchWeather = async () => {
    try {
      setLoading(true);
      setError(null);

      // 🧪 TEST DATA BYPASS (matches current-trip page pattern)
      const USE_TEST_DATA = true;
      const FAKE_WEATHER = {
        'KJFK': {
          airport: 'KJFK',
          flight_category: 'VFR',
          temperature_f: 57,
          conditions: {
            temperature_f: 57,
            ceiling_ft: null,
            visibility_sm: 10,
            wind: '270@10',
            altimeter: 30.12,
            weather: 'Clear'
          },
          altimeter: 30.12,
          ceiling: null,
          visibility: 10,
          wind: '270@10',
          weather: 'Clear',
          trend: null,
          alerts: [],
          metar_raw: 'KJFK 261651Z 27010KT 10SM CLR 14/M02 A3012'
        },
        'KLAX': {
          airport: 'KLAX',
          flight_category: 'VFR',
          temperature_f: 72,
          conditions: {
            temperature_f: 72,
            ceiling_ft: null,
            visibility_sm: 10,
            wind: '240@08',
            altimeter: 29.98,
            weather: 'Few Clouds'
          },
          altimeter: 29.98,
          ceiling: null,
          visibility: 10,
          wind: '240@08',
          weather: 'Few Clouds',
          trend: null,
          alerts: [],
          metar_raw: 'KLAX 261653Z 24008KT 10SM FEW030 22/12 A2998'
        },
        'KATL': {
          airport: 'KATL',
          flight_category: 'MVFR',
          temperature_f: 64,
          conditions: {
            temperature_f: 64,
            ceiling_ft: 2500,
            visibility_sm: 5,
            wind: '180@12',
            altimeter: 30.05,
            weather: 'Broken Clouds'
          },
          altimeter: 30.05,
          ceiling: 2500,
          visibility: 5,
          wind: '180@12',
          weather: 'Broken Clouds',
          trend: 'worsening',
          alerts: ['AIRMET for moderate turbulence below 10,000 feet'],
          metar_raw: 'KATL 261651Z 18012KT 5SM BKN025 18/12 A3005'
        }
      };

      if (USE_TEST_DATA) {
        console.log(`[WeatherCard] 🧪 USING FAKE WEATHER for ${icao}`);
        const fakeWeather = FAKE_WEATHER[icao.toUpperCase()] || FAKE_WEATHER['KJFK'];
        setWeatherData(normalizeWeatherData(fakeWeather));
        setLastUpdate(new Date());
        setLoading(false);
        return;
      }

      const response = await fetch(`http://localhost:8000/api/v1/weather/airport/${icao}`);
      
      if (!response.ok) {
        throw new Error(`Weather fetch failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setWeatherData(normalizeWeatherData(data));
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Weather fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and auto-refresh setup
  useEffect(() => {
    fetchWeather();

    if (autoRefresh) {
      const interval = setInterval(fetchWeather, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [icao, autoRefresh, refreshInterval]);

  // Get color scheme for flight category
  const getFlightCategoryColor = (categoryObj) => {
    // Handle both object format (from API) and string format (fallback)
    const categoryClass = typeof categoryObj === 'object' ? categoryObj.class : categoryObj;
    
    const colors = {
      VFR: { bg: '#4caf50', text: '#fff' },      // Green
      MVFR: { bg: '#2196f3', text: '#fff' },     // Blue
      IFR: { bg: '#ff9800', text: '#fff' },      // Orange
      LIFR: { bg: '#f44336', text: '#fff' },     // Red
      UNKNOWN: { bg: '#757575', text: '#fff' },  // Gray
    };
    return colors[categoryClass] || { bg: '#757575', text: '#fff' }; // Gray default
  };

  // Get weather condition icon based on flight category and weather
  const getWeatherIcon = (flightCategory, weather) => {
    const categoryClass = typeof flightCategory === 'object' ? flightCategory.class : flightCategory;
    const weatherStr = (weather || '').toLowerCase();
    
    // Specific weather conditions take priority
    if (weatherStr.includes('thunder') || weatherStr.includes('tstm')) {
      return <WiThunderstorm size={48} />;
    }
    if (weatherStr.includes('snow')) {
      return <WiSnow size={48} />;
    }
    if (weatherStr.includes('rain')) {
      return <WiRain size={48} />;
    }
    if (weatherStr.includes('fog') || weatherStr.includes('mist')) {
      return <WiFog size={48} />;
    }
    
    // Flight category-based icons
    if (categoryClass === 'VFR') {
      return <WiDaySunny size={48} style={{ color: '#FFD700' }} />;
    }
    if (categoryClass === 'MVFR') {
      return <WiDayCloudyHigh size={48} style={{ color: '#2196f3' }} />;
    }
    if (categoryClass === 'IFR') {
      return <WiCloudy size={48} style={{ color: '#ff9800' }} />;
    }
    if (categoryClass === 'LIFR') {
      return <WiFog size={48} style={{ color: '#f44336' }} />;
    }
    
    // Default
    return <WiCloudy size={48} />;
  };

  // Get trend icon based on weather trend
  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingUpIcon sx={{ color: '#4caf50' }} />;
    if (trend === 'worsening') return <TrendingDownIcon sx={{ color: '#f44336' }} />;
    return <TrendingFlatIcon sx={{ color: '#757575' }} />;
  };

  // Format last update timestamp
  const formatLastUpdate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const now = new Date();
    const diff = Math.floor((now - timestamp) / 1000); // seconds
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} mins ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    return timestamp.toLocaleTimeString();
  };

  // Loading state
  if (loading && !weatherData) {
    return (
      <Card sx={{ minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Card>
    );
  }

  // Error state
  if (error && !weatherData) {
    return (
      <Card sx={{ minHeight: 300 }}>
        <CardContent>
          <Alert 
            severity="error" 
            action={
              <Chip 
                label="Retry" 
                icon={<RefreshIcon />}
                onClick={fetchWeather}
                size="small"
              />
            }
          >
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // No data state
  if (!weatherData) {
    return (
      <Card sx={{ minHeight: 300 }}>
        <CardContent>
          <Alert severity="info">No weather data available for {icao}</Alert>
        </CardContent>
      </Card>
    );
  }

  const flightCategoryColors = getFlightCategoryColor(weatherData.flight_category);
  const weatherIcon = getWeatherIcon(weatherData.flight_category, weatherData.weather);
  const trendIcon = getTrendIcon(weatherData.trend);
  
  // Extract label from flight_category object (API returns {class, color, label})
  const flightCategoryLabel = typeof weatherData.flight_category === 'object' 
    ? weatherData.flight_category.label 
    : weatherData.flight_category;

  return (
    <Card
      sx={{
        minHeight: 300,
        borderLeft: `3px solid ${flightCategoryColors.bg}`,
        boxShadow: 3,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
        },
      }}
    >
      <CardContent>
        {/* Header: Title, Weather Icon, and Flight Category */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {weatherIcon}
            <Typography variant="h6" component="div">
              {title || icao}
            </Typography>
          </Box>
          <Chip
            label={flightCategoryLabel}
            sx={{
              backgroundColor: flightCategoryColors.bg,
              color: flightCategoryColors.text,
              fontWeight: 'bold',
              fontSize: '1rem',
            }}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Current Conditions Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 2,
            mb: 2,
          }}
        >
          {/* Ceiling */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WiHorizonAlt size={32} style={{ color: '#757575' }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Ceiling
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {weatherData.ceiling ? `${weatherData.ceiling.toLocaleString()} ft` : 'N/A'}
              </Typography>
            </Box>
          </Box>

          {/* Visibility */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WiFog size={32} style={{ color: '#757575' }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Visibility
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {weatherData.visibility ? `${weatherData.visibility} SM` : 'N/A'}
              </Typography>
            </Box>
          </Box>

          {/* Wind */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WiStrongWind size={32} style={{ color: '#757575' }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Wind
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {weatherData.wind ? weatherData.wind : 'N/A'}
              </Typography>
            </Box>
          </Box>

          {/* Temperature */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WiThermometer size={32} style={{ color: '#757575' }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Temperature
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {weatherData.temperature_c !== null && weatherData.temperature_f !== null
                  ? `${weatherData.temperature_c}°C / ${Math.round(weatherData.temperature_f)}°F`
                  : 'N/A'}
              </Typography>
            </Box>
          </Box>

          {/* Altimeter */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WiBarometer size={32} style={{ color: '#757575' }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Altimeter
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {weatherData.altimeter_inhg !== null && weatherData.altimeter_hpa !== null
                  ? `${weatherData.altimeter_inhg.toFixed(2)} inHg / ${weatherData.altimeter_hpa} hPa`
                  : 'N/A'}
              </Typography>
            </Box>
          </Box>

          {/* Weather */}
          {weatherData.weather && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon sx={{ color: 'warning.main' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Weather
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {weatherData.weather}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* METAR & TAF Section - Moved Up */}
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          {/* Raw METAR */}
          {weatherData.raw_text && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom fontWeight="medium">
                METAR:
              </Typography>
              <Typography
                variant="body2"
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  color: 'text.secondary',
                  mb: 0,
                }}
              >
                {weatherData.raw_text}
              </Typography>
            </Box>
          )}

          {/* TAF Placeholder */}
          {weatherData.raw_text && (
            <Box>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom fontWeight="medium">
                TAF:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                TAF data not yet available (future integration)
              </Typography>
            </Box>
          )}
        </Box>

        {/* Trend Indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          {trendIcon}
          <Typography variant="body2" color="text.secondary">
            {weatherData.trend === 'improving' && 'Conditions improving'}
            {weatherData.trend === 'stable' && 'Conditions stable'}
            {weatherData.trend === 'worsening' && 'Conditions worsening'}
          </Typography>
        </Box>

        {/* Alerts */}
        {weatherData.alerts && weatherData.alerts.length > 0 && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="medium">
              Active Alerts:
            </Typography>
            {weatherData.alerts.map((alert, index) => (
              <Typography key={index} variant="body2">
                • {alert}
              </Typography>
            ))}
          </Alert>
        )}

        {/* Footer: Last Update */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Updated {formatLastUpdate(lastUpdate)}
          </Typography>
        </Box>

        {/* Refresh Button - Moved to Bottom */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Chip
            label="Refresh Weather"
            icon={<RefreshIcon />}
            onClick={fetchWeather}
            size="small"
            clickable
            sx={{ 
              cursor: 'pointer',
              width: '100%',
              '&:hover': {
                backgroundColor: 'primary.light',
              }
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default WeatherCard;
